import ConfigurationService from '../services/ConfigurationService';
import { isRetryableError, getRetryClassification } from './errorHandler';
import { NetworkError, RetryTelemetry } from '../services/interfaces/network.types';
import logger from '../services/loggerService';


/**
 * Options for configuring retry behavior
 */
export interface RetryOptions {
  /**
   * Maximum number of retry attempts (default: 3)
   */
  maxRetries?: number;

  /**
   * Initial delay in milliseconds (default: 1000ms = 1s)
   */
  initialDelayMs?: number;

  /**
   * Maximum delay in milliseconds (default: 30000ms = 30s)
   */
  maxDelayMs?: number;

  /**
   * Multiplier for exponential backoff (default: 2)
   */
  backoffMultiplier?: number;

  /**
   * Custom function to determine if an error should be retried
   * If not provided, uses isRetryableError from errorHandler
   */
  shouldRetry?: (error: unknown) => boolean;

  /**
   * Callback invoked before each retry attempt
   */
  onRetry?: (attempt: number, error: unknown) => void;

  /**
   * Optional AbortSignal to cancel retry operations immediately.
   */
  signal?: AbortSignal;

  /**
   * Total timeout deadline in milliseconds for all retry attempts combined.
   */
  deadlineMs?: number;
}

/**
 * Delays execution for a specified number of milliseconds (cancelable via AbortSignal)
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(signal.reason || new DOMException('The user aborted a request.', 'AbortError'));
    }

    const timer = setTimeout(() => {
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
      resolve();
    }, ms);

    function onAbort() {
      clearTimeout(timer);
      reject(signal?.reason || new DOMException('The user aborted a request.', 'AbortError'));
    }

    if (signal) {
      signal.addEventListener('abort', onAbort);
    }
  });
}


/**
 * Extracts the Retry-After header delay in milliseconds, if present.
 */
function getRetryAfterDelay(error: unknown): number | null {
  if (error && typeof error === 'object' && 'response' in error) {
    const netError = error as NetworkError;
    if (netError.response?.headers) {
      // Axios headers are typically lowercased
      const retryAfter = netError.response.headers['retry-after'] || netError.response.headers['Retry-After'];
      if (retryAfter) {
        const parsedSecs = parseInt(retryAfter, 10);
        if (!isNaN(parsedSecs)) {
          return parsedSecs * 1000;
        }
        const parsedDate = new Date(retryAfter).getTime();
        if (!isNaN(parsedDate)) {
          return Math.max(0, parsedDate - Date.now());
        }
      }
    }
  }
  return null;
}

/**
 * Calculates the delay for the next retry attempt using full jitter exponential backoff
 */
function calculateDelay(
  attempt: number,
  initialDelay: number,
  maxDelay: number,
  multiplier: number,
  error?: unknown
): number {
  const retryAfter = getRetryAfterDelay(error);
  if (retryAfter !== null && retryAfter > 0) {
    return retryAfter;
  }

  const exponentialDelay = initialDelay * Math.pow(multiplier, attempt - 1);
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  // Full Jitter algorithm: random delay between 0 and the capped backoff
  return Math.floor(Math.random() * (cappedDelay + 1));
}

/**
 * Retries an asynchronous operation with exponential backoff and tracks telemetric data.
 *
 * @param operation - The async function to retry
 * @param options - Retry configuration options
 * @param correlationId - Optional correlation ID for correlation
 * @returns Promise resolving to the data and telemetry details
 * @throws The last error if all retries are exhausted or non-retryable
 */
export async function retryWithBackoffAndTelemetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {},
  correlationId?: string
): Promise<{ data: T; telemetry: RetryTelemetry }> {
  const {
    shouldRetry = isRetryableError,
    onRetry,
    signal,
  } = options;

  // Enhance dynamic config fallback if needed
  const retryConfig = ConfigurationService.getRetryConfig();
  const config = retryConfig?.standard;
  const globalConfig = retryConfig?.global;
  const effectiveMaxRetries = options.maxRetries ?? config?.maxRetries ?? 3;
  const effectiveInitialDelayMs = options.initialDelayMs ?? config?.initialDelayMs ?? 1000;
  const effectiveMaxDelayMs = options.maxDelayMs ?? config?.maxDelayMs ?? 30000;
  const effectiveMultiplier = options.backoffMultiplier ?? config?.backoffMultiplier ?? 2;
  const deadlineMs = options.deadlineMs ?? globalConfig?.defaultDeadlineMs ?? 60000;
  const telemetryEnabled = globalConfig?.enableTelemetry !== false;

  const actualCorrelationId = correlationId || (options as Record<string, unknown>).correlationId as string || `retry-${Math.random().toString(36).substring(2, 11)}`;

  /** Conditionally log telemetry based on dynamic config toggle. */
  const logTelemetry = (message: string, telemetry: RetryTelemetry): void => {
    if (telemetryEnabled) {
      logger.info(message, { context: 'RetryTelemetry', ...telemetry });
    }
  };

  const startTime = Date.now();
  const retryReasons: string[] = [];
  const timestamps: number[] = [];
  let totalAttempts = 0;
  let lastError: unknown;

  for (let attempt = 1; attempt <= effectiveMaxRetries + 1; attempt++) {
    // Check AbortSignal before attempt
    if (signal?.aborted) {
      const duration = Date.now() - startTime;
      const telemetry: RetryTelemetry = {
        totalAttempts,
        retryReasons,
        totalDurationMs: duration,
        finalResult: 'cancelled',
        correlationId: actualCorrelationId,
        timestamps,
      };
      logTelemetry('Retry operations telemetry (cancelled)', telemetry);
      throw signal.reason || new DOMException('The user aborted a request.', 'AbortError');
    }

    // Check overall deadline before attempt
    const elapsed = Date.now() - startTime;
    if (deadlineMs && elapsed >= deadlineMs) {
      const duration = Date.now() - startTime;
      const telemetry: RetryTelemetry = {
        totalAttempts,
        retryReasons,
        totalDurationMs: duration,
        finalResult: 'exhausted',
        correlationId: actualCorrelationId,
        timestamps,
      };
      logTelemetry('Retry operations telemetry (deadline exceeded)', telemetry);
      throw lastError || new Error('Retry deadline exceeded');
    }

    try {
      totalAttempts = attempt;
      timestamps.push(Date.now());
      const data = await operation();

      const duration = Date.now() - startTime;
      const telemetry: RetryTelemetry = {
        totalAttempts,
        retryReasons,
        totalDurationMs: duration,
        finalResult: 'success',
        correlationId: actualCorrelationId,
        timestamps,
      };
      logTelemetry('Retry operations telemetry (success)', telemetry);
      return { data, telemetry };
    } catch (error) {
      lastError = error;

      // Check classification
      const classification = getRetryClassification(error);
      retryReasons.push(classification.reason);

      // Don't retry if this is the last attempt
      if (attempt > effectiveMaxRetries) {
        const duration = Date.now() - startTime;
        const telemetry: RetryTelemetry = {
          totalAttempts,
          retryReasons,
          totalDurationMs: duration,
          finalResult: 'exhausted',
          correlationId: actualCorrelationId,
          timestamps,
        };
        logTelemetry('Retry operations telemetry (exhausted)', telemetry);
        break;
      }

      // Don't retry if the error is not retryable
      if (!shouldRetry(error)) {
        const duration = Date.now() - startTime;
        const telemetry: RetryTelemetry = {
          totalAttempts,
          retryReasons,
          totalDurationMs: duration,
          finalResult: 'non_retryable',
          correlationId: actualCorrelationId,
          timestamps,
        };
        logTelemetry('Retry operations telemetry (non_retryable)', telemetry);
        throw error;
      }

      // Calculate delay
      const delayMs = calculateDelay(attempt, effectiveInitialDelayMs, effectiveMaxDelayMs, effectiveMultiplier, error);

      // Check if waiting would exceed overall deadline budget
      if (deadlineMs && (Date.now() - startTime + delayMs) > deadlineMs) {
        const duration = Date.now() - startTime;
        const telemetry: RetryTelemetry = {
          totalAttempts,
          retryReasons,
          totalDurationMs: duration,
          finalResult: 'exhausted',
          correlationId: actualCorrelationId,
          timestamps,
        };
        logTelemetry('Retry operations telemetry (delay exceeds deadline)', telemetry);
        throw error;
      }

      if (onRetry) {
        onRetry(attempt, error);
      } else {
        logger.warn(`Retry attempt ${attempt}/${effectiveMaxRetries} after ${delayMs}ms delay`, {
          context: 'retryWithBackoff',
          attempt,
          delayMs,
          correlationId: actualCorrelationId,
          error: error instanceof Error ? error.message : String(error)
        });
      }

      // Wait before retrying (cancelable via AbortSignal)
      try {
        await delay(delayMs, signal);
      } catch (delayError) {
        // If aborted during delay, log telemetry and throw
        const duration = Date.now() - startTime;
        const telemetry: RetryTelemetry = {
          totalAttempts,
          retryReasons,
          totalDurationMs: duration,
          finalResult: 'cancelled',
          correlationId: actualCorrelationId,
          timestamps,
        };
        logTelemetry('Retry operations telemetry (aborted during delay)', telemetry);
        throw delayError;
      }
    }
  }

  // All retries exhausted, throw the last error
  throw lastError;
}

/**
 * Retries an asynchronous operation with exponential backoff.
 *
 * @param operation - The async function to retry
 * @param options - Retry configuration options
 * @returns Promise resolving to the operation result
 * @throws The last error if all retries are exhausted
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const result = await retryWithBackoffAndTelemetry(operation, options);
  return result.data;
}


/**
 * Creates a retryable version of an async function.
 *
 * @param fn - The function to make retryable
 * @param options - Retry configuration options
 * @returns A new function that retries on failure
 *
 * @example
 * const fetchUsers = retryable(
 *   () => apiClient.get('/users'),
 *   { maxRetries: 3 }
 * );
 *
 * const users = await fetchUsers();
 */
export function retryable<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  options: RetryOptions = {}
): (...args: Args) => Promise<R> {
  return (...args: Args): Promise<R> => {
    return retryWithBackoff(() => fn(...args), options);
  };
}

/**
 * Gets retry configuration presets for common scenarios.
 * All values are loaded dynamically from ConfigurationService.
 *
 * @returns Object containing retry presets (QUICK, STANDARD, PATIENT, AGGRESSIVE)
 *
 * @example
 * const presets = getRetryPresets();
 * await retryWithBackoff(() => apiClient.get('/data'), presets.STANDARD);
 */
export function getRetryPresets() {
  const config = ConfigurationService.getRetryConfig();

  return {
    /**
     * Quick retries for real-time operations
     */
    QUICK: config.quick as RetryOptions,

    /**
     * Standard retries for typical API calls
     */
    STANDARD: config.standard as RetryOptions,

    /**
     * Patient retries for background operations
     */
    PATIENT: config.patient as RetryOptions,

    /**
     * Aggressive retries for critical operations
     */
    AGGRESSIVE: config.aggressive as RetryOptions,
  };
}
